import React from "react";
import {
  Autocomplete,
  autocompleteClasses,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListSubheader,
  Popper,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import nuclides from "core/services/nuclideService";
import { VariableSizeList } from "react-window";

const SearchDialog = ({ setSelectedNuclide }) => {
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Tooltip title="Search Isotopes">
        <IconButton
          color="inherit"
          size="small"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Search />
        </IconButton>
      </Tooltip>
      <Dialog
        sx={{ ".MuiDialog-container": { alignItems: "flex-start" } }}
        onClose={handleClose}
        open={open}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack spacing={1} direction="row" width="100%" alignItems="center">
            <Typography width="100%" variant="h6" fontWeight="bold">
              Search Isotopes
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Autocomplete
            PopperComponent={StyledPopper}
            id="virtualize-search"
            fullWidth
            disableListWrap
            autoComplete={false}
            openOnFocus
            blurOnSelect
            onChange={(_, opt) => {
              if (opt) {
                setSelectedNuclide(opt);
              }
            }}
            onBlur={() => {
              handleClose();
            }}
            ListboxComponent={ListboxComponent}
            options={nuclides.flatten}
            getOptionLabel={(n) => `${n.name}:${n.symbol}`}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  autoCorrect: "off",
                  spellCheck: false,
                }}
                label="Type Any Isotope Name or Symbol"
                autoFocus
              />
            )}
            renderOption={(props, option) => [props, option]}
            // TODO: Post React 18 update - validate this conversion, look like a hidden bug
            renderGroup={(params) => params}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  };

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      <span>{dataSet[1].name}</span>
      <b>{dataSet[1].symbol}</b>
    </Typography>
  );
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty("group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

export default SearchDialog;
